import { PRODUCT_IDS, PRODUCT_BUNDLE_IDS, PRODUCT_BUNDLES } from "./constants";
import {
  getComplexFieldValue,
  dateRangeControlFormatter,
  dateRangeAPIFormatter,
  dateRangeValidator,
  selectedStatesValidator,
} from "./utilities";
import { isNullOperator } from "core/components/QueryBuilder/utilities";

export const PRODUCTS = {
  [PRODUCT_IDS.IDENTIFIED_CONSUMERS]: {
    id: PRODUCT_IDS.IDENTIFIED_CONSUMERS,
    dataTableKey: "consumer",
    meta: {
      name: "Identified Consumer Records",
      description:
        "Enable to license identified consumer records. Requires configuration of purchased attribute limit, specifically purchased attributes, total record limit, and filters to keep record count below record limit.",
      bundle: PRODUCT_BUNDLES[PRODUCT_BUNDLE_IDS.POP_INTEL],
    },
    page: {
      path: "identified-consumer-records",
      isEnabled: {
        fieldKey: "isEnabled",
      },
      settings: [
        {
          title: "Anonymize Data",
          description:
            "Optionally deploy anonymized consumer records in place of identified consumer records. This is often used in early stages on onboarding where the customer has requested anonymized data for testing that mirrors real world data.",
          controls: [
            {
              fieldKey: "isAnonymized",
              componentType: "toggle",
              staticProps: {},
            },
          ],
        },
        {
          title: "Purchased Attributes",
          description:
            "Required configuration. Set the total number of purchased attributes available to the customer and allows selection of purchased attributes. The total number of selected attributes must be below the selected attribute limit. It is not required that all attributes be selected at time of configuration.",
          controls: [
            {
              fieldKey: "attributeLimit",
              componentType: "number",
              staticProps: {
                label: "Attribute Limit",
                placeholder: "Attribute Limit",
                name: "attributeLimit",
              },
            },
            {
              fieldKey: "purchasedAttributes",
              componentType: "mms",
              staticProps: {
                label: "Selected Attributes",
                placeholder: "Select Attributes",
                name: "purchasedAttributes",
              },
            },
          ],
        },
        {
          title: "Consumer Records",
          description:
            "Required configuration. Set the total number of consumer records licensed to the customer. This number must be greater than zero.",
          controls: [
            {
              fieldKey: "recordLimit",
              componentType: "number",
              staticProps: {
                label: "Record Limit",
                placeholder: "Record Limit",
                name: "recordLimit",
              },
            },
            {
              fieldKey: null,
              componentType: "subtitle",
              staticProps: {
                text: "Consumer Filters",
              },
            },
            {
              fieldKey: null,
              componentType: "description",
              staticProps: {
                text: "Required configuration. This configuration requires the specific States and Zip Codes to limit the consumer records. Optionally, the activation filters” section provides more complex query building operations allowing the user to flexibly limit identified consumer records based on any attributes within the consumer data (e.g. Age between 25 and 80”).",
              },
            },
            {
              fieldKey: "states",
              componentType: "combobox",
              toggleKey: "isStateFilteringRequired",
              staticProps: {
                label: "Selected States",
                placeholder: "Select States",
                name: "states",
                isMultiSelect: true,
                isSearchable: true,
                toggleLabel: "State Selection",
                multiValueMaxDisplayCount: 15,
              },
            },
            {
              fieldKey: "zipcodes",
              componentType: "bulkInput",
              staticProps: {
                label: "Zip Codes",
              },
            },
            {
              fieldKey: "consumerFilters",
              componentType: "queryBuilder",
              staticProps: {
                label: "Activation Filters",
              },
            },
          ],
        },
      ],
    },
    fields: {
      isEnabled: {
        type: "boolean", // used to build valdataIdators later
        dataId: "enabled", // path to value in product object
      },
      isAnonymized: {
        type: "boolean",
        dataId: "license.anonymized_records",
      },
      attributeLimit: {
        dataId: "license.identified_consumer_attribute_limit",
        type: "number",
        validator: ({ product, field }) => {
          const { data, fields, fieldErrors } = product;
          const { fieldValue } = field;

          const purchasedAttributesDataId = fields.purchasedAttributes.dataId;
          const purchasedAttributesValue = getComplexFieldValue({
            dataId: purchasedAttributesDataId,
            data: data,
          });

          if (purchasedAttributesValue.length > fieldValue) {
            return [
              false,
              {
                ...fieldErrors,
                attributeLimit:
                  "Limit must be increased to support Purchased Attributes.",
              },
            ];
          }

          return [
            true,
            {
              ...fieldErrors,
              attributeLimit: "",
              purchasedAttributes: "",
            },
          ];
        },
      },
      purchasedAttributes: {
        dataId: "license.data.consumer.licensed_field_group_ids",
        type: "stringArray",
        validator: ({ product, field }) => {
          const { data, fields, fieldErrors } = product;
          const { fieldValue } = field;

          const attributeLimitDataId = fields.attributeLimit.dataId;
          const attributeLimitValue = getComplexFieldValue({
            dataId: attributeLimitDataId,
            data: data,
          });

          if (attributeLimitValue < fieldValue.length) {
            return [
              false,
              {
                ...fieldErrors,
                purchasedAttributes: "Number selected exceeds Attribute Limit.",
              },
            ];
          }

          return [
            true,
            {
              ...fieldErrors,
              attributeLimit: "",
              purchasedAttributes: "",
            },
          ];
        },
      },
      recordLimit: {
        dataId: "license.identified_consumer_record_limit",
        type: "number",
      },
      states: {
        dataId: "license.geography.states",
        type: "stringArray",
        validator: selectedStatesValidator,
      },
      zipcodes: {
        dataId: "license.geography.zips",
        type: "stringArray",
      },
      consumerFilters: {
        dataId: "license.identified_consumer_filters",
        type: "objectArray",
        validator: ({ product, field }) => {
          const { fieldErrors } = product;
          const { fieldValue } = field;

          if (!Array.isArray(fieldValue)) {
            return [
              false,
              {
                ...fieldErrors,
                consumerFilters: "Consumer filters should be an array.",
              },
            ];
          }

          const deepCheckGroupRules = (group, check) => {
            return group.filters.every((filter) => {
              if (Object.hasOwn(filter, "filters")) {
                return deepCheckGroupRules(filter, check);
              } else {
                return check(filter);
              }
            });
          };

          const deepCheckGroup = (group) => {
            return (
              group.filters.length > 0 &&
              group.filters.every((filter) => {
                if (Object.hasOwn(filter, "filters")) {
                  return deepCheckGroup(filter);
                } else {
                  return true;
                }
              })
            );
          };

          const validateRule = (rule) => {
            if (Object.hasOwn(rule, "value")) {
              if (isNullOperator(rule.operator)) return rule.value === "";
              return rule.value !== "";
            }

            if (Object.hasOwn(rule, "from") || Object.hasOwn(rule, "to")) {
              return (
                Object.hasOwn(rule, "from") &&
                Object.hasOwn(rule, "to") &&
                rule.from !== "" &&
                rule.to !== ""
              );
            }

            return false;
          };

          const allGroupsHaveFilter = fieldValue
            .reduce((checks, currentFilter) => {
              if (Object.hasOwn(currentFilter, "filters")) {
                return checks.concat(deepCheckGroup(currentFilter));
              } else {
                return checks;
              }
            }, [])
            .every((groupHasFilters) => groupHasFilters === true);

          if (!allGroupsHaveFilter) {
            return [
              false,
              {
                ...fieldErrors,
                consumerFilters:
                  "Consumer filters cannot contain empty groups.",
              },
            ];
          }

          const allRulesContainValues = fieldValue
            .reduce((checks, currentFilter) => {
              if (Object.hasOwn(currentFilter, "filters")) {
                return checks.concat(
                  deepCheckGroupRules(currentFilter, validateRule)
                );
              } else {
                return checks.concat(validateRule(currentFilter));
              }
            }, [])
            .every((ruleHasValue) => ruleHasValue === true);

          if (!allRulesContainValues) {
            return [
              false,
              {
                ...fieldErrors,
                consumerFilters:
                  "Consumer filters cannot contain rules without values.",
              },
            ];
          }

          return [
            true,
            {
              ...fieldErrors,
              consumerFilters: "",
            },
          ];
        },
      },
    },
  },
  [PRODUCT_IDS.DE_IDENTIFIED_CONSUMERS]: {
    id: PRODUCT_IDS.DE_IDENTIFIED_CONSUMERS,
    dataTableKey: null,
    meta: {
      name: "Population Insights",
      description:
        "Enable to license de-identified consumer records and all related dashboards (e.g. Population Insights”). Optionally limit the de-identified consumer records to one or more selected States.",
      bundle: PRODUCT_BUNDLES[PRODUCT_BUNDLE_IDS.POP_INTEL],
    },
    page: {
      path: "population_insights",
      isEnabled: {
        fieldKey: "isEnabled",
      },
      settings: [
        {
          title: "Consumer Data",
          description:
            "Enable to license de-identified consumer records and all related dashboards (e.g. Population Insights”). Optionally limit the de-identified consumer records to one or more selected States.",
          controls: [
            {
              fieldKey: "states",
              componentType: "combobox",
              toggleKey: "isStateFilteringRequired",
              staticProps: {
                label: "Selected States",
                toggleLabel: "State Selection",
                placeholder: "Select States",
                name: "states",
                isMultiSelect: true,
                isSearchable: true,
                multiValueMaxDisplayCount: 15,
              },
            },
          ],
        },
      ],
    },
    fields: {
      isEnabled: {
        dataId: "enabled",
        type: "boolean",
      },
      states: {
        dataId: "license.geography.states",
        type: "stringArray",
        validator: selectedStatesValidator,
      },
    },
  },
  [PRODUCT_IDS.PROPENSITY_MODELS]: {
    id: PRODUCT_IDS.PROPENSITY_MODELS,
    dataTableKey: null, // ? unknown
    meta: {
      name: "Propensity Models",
      description:
        "Enable to license propensity models which are appended to the customers identified consumer data. Requires configuration of total purchased model limit and selection of specific propensity models. ",
      bundle: PRODUCT_BUNDLES[PRODUCT_BUNDLE_IDS.POP_INTEL],
    },
    page: {
      path: "propensity-models",
      isEnabled: {
        fieldKey: "isEnabled",
      },
      settings: [
        {
          title: "Propensity Models",
          description:
            "'Purchased Model Limit' sets the total number of propensity models licensed in the customers contract. This number must be greater than 1. ‘Selected Propensity Models’ sets the specific models the customer has selected. The number of selected propensity models can not exceed the purchased model limit. It is not required that any or all models be selected at time of configuration.",
          controls: [
            {
              fieldKey: "modelLimit",
              componentType: "number",
              staticProps: {
                label: "Purchased Model Limit",
                placeholder: "Purchased Model Limit",
              },
            },
            {
              fieldKey: "selectedModels",
              componentType: "combobox",
              staticProps: {
                label: "Selected Propensity Models",
                placeholder: "Select Propensity Models",
                name: "selectedModels",
                isMultiSelect: true,
                isSearchable: true,
                multiValueMaxDisplayCount: 15,
              },
            },
          ],
        },
      ],
    },
    fields: {
      isEnabled: {
        dataId: "enabled",
        type: "boolean",
      },
      // Mirrors the attributeLimit/purchasedAttributes pair above: each side
      // validates against the other's current value, so the user gets the
      // error whichever field they edit into conflict.
      modelLimit: {
        dataId: "license.model_limit",
        type: "number",
        validator: ({ product, field }) => {
          const { data, fields, fieldErrors } = product;
          const { fieldValue } = field;

          const selectedModelsValue = getComplexFieldValue({
            dataId: fields.selectedModels.dataId,
            data: data,
          });

          if (selectedModelsValue.length > fieldValue) {
            return [
              false,
              {
                ...fieldErrors,
                modelLimit:
                  "Limit must be increased to support Selected Propensity Models.",
              },
            ];
          }

          return [
            true,
            {
              ...fieldErrors,
              modelLimit: "",
              selectedModels: "",
            },
          ];
        },
      },
      selectedModels: {
        dataId: "license.selected_models",
        type: "stringArray",
        validator: ({ product, field }) => {
          const { data, fields, fieldErrors } = product;
          const { fieldValue } = field;

          const modelLimitValue = getComplexFieldValue({
            dataId: fields.modelLimit.dataId,
            data: data,
          });

          if (modelLimitValue < fieldValue.length) {
            return [
              false,
              {
                ...fieldErrors,
                selectedModels:
                  "Number selected exceeds Purchased Model Limit.",
              },
            ];
          }

          return [
            true,
            {
              ...fieldErrors,
              modelLimit: "",
              selectedModels: "",
            },
          ];
        },
      },
    },
  },
  [PRODUCT_IDS.NEW_MOVERS]: {
    id: PRODUCT_IDS.NEW_MOVERS,
    dataTableKey: "consumer_new_mover",
    meta: {
      name: "New Movers",
      description:
        "Enable to license new mover records. Requires configuration of new mover record limit and filters to keep record count below record limit.",
      bundle: PRODUCT_BUNDLES[PRODUCT_BUNDLE_IDS.POP_INTEL],
    },
    page: {
      path: "new-movers",
      isEnabled: {
        fieldKey: "isEnabled",
      },
      settings: [
        {
          title: "New Mover Records",
          description:
            "Required configuration. Set the total number of consumer records licensed to the customer. This number must be greater than zero.",
          controls: [
            {
              fieldKey: "recordLimit",
              componentType: "number",
              staticProps: {
                name: "recordLimit",
                placeholder: "Record Limit",
                label: "Record Limit",
              },
            },
            {
              fieldKey: null,
              componentType: "subtitle",
              staticProps: {
                text: "New Mover Filters",
              },
            },
            {
              fieldKey: null,
              componentType: "description",
              staticProps: {
                text: "Required configuration. This configuration requires the specific States and Zip Codes to limit the new mover records.",
              },
            },
            {
              fieldKey: "states",
              componentType: "combobox",
              toggleKey: "isStateFilteringRequired",
              staticProps: {
                label: "Selected States",
                toggleLabel: "State Selection",
                placeholder: "Select States",
                name: "states",
                isMultiSelect: true,
                isSearchable: true,
                multiValueMaxDisplayCount: 15,
              },
            },
            {
              fieldKey: "moverFilters",
              componentType: "queryBuilder",
              staticProps: {
                label: "Activation Filters",
              },
            },
            // {
            //   fieldKey: "moverFilters",
            //   componentType: "custom",
            //   customComponent: NewMoversFilters,
            // },
          ],
        },
      ],
    },
    fields: {
      isEnabled: {
        dataId: "enabled",
        type: "boolean",
      },
      recordLimit: {
        dataId: "license.new_mover_record_limit",
        type: "number",
      },
      moverFilters: {
        dataId: "license.new_mover_filters",
        type: "objectArray",
        validator: ({ product, field }) => {
          const { fieldErrors } = product;
          const { fieldValue } = field;

          if (!Array.isArray(fieldValue)) {
            return [
              false,
              {
                ...fieldErrors,
                moverFilters: "Mover filters should be an array.",
              },
            ];
          }

          const deepCheckGroupRules = (group, check) => {
            return group.filters.every((filter) => {
              if (Object.hasOwn(filter, "filters")) {
                return deepCheckGroupRules(filter, check);
              } else {
                return check(filter);
              }
            });
          };

          const deepCheckGroup = (group) => {
            return (
              group.filters.length > 0 &&
              group.filters.every((filter) => {
                if (Object.hasOwn(filter, "filters")) {
                  return deepCheckGroup(filter);
                } else {
                  return true;
                }
              })
            );
          };

          const validateRule = (rule) => {
            if (Object.hasOwn(rule, "value")) {
              if (isNullOperator(rule.operator)) return rule.value === "";
              return rule.value !== "";
            }

            if (Object.hasOwn(rule, "from") || Object.hasOwn(rule, "to")) {
              return (
                Object.hasOwn(rule, "from") &&
                Object.hasOwn(rule, "to") &&
                rule.from !== "" &&
                rule.to !== ""
              );
            }

            return false;
          };

          const allGroupsHaveFilter = fieldValue
            .reduce((checks, currentFilter) => {
              if (Object.hasOwn(currentFilter, "filters")) {
                return checks.concat(deepCheckGroup(currentFilter));
              } else {
                return checks;
              }
            }, [])
            .every((groupHasFilters) => groupHasFilters === true);

          if (!allGroupsHaveFilter) {
            return [
              false,
              {
                ...fieldErrors,
                moverFilters: "Mover filters cannot contain empty groups.",
              },
            ];
          }

          const allRulesContainValues = fieldValue
            .reduce((checks, currentFilter) => {
              if (Object.hasOwn(currentFilter, "filters")) {
                return checks.concat(
                  deepCheckGroupRules(currentFilter, validateRule)
                );
              } else {
                return checks.concat(validateRule(currentFilter));
              }
            }, [])
            .every((ruleHasValue) => ruleHasValue === true);

          if (!allRulesContainValues) {
            return [
              false,
              {
                ...fieldErrors,
                moverFilters:
                  "Mover filters cannot contain rules without values.",
              },
            ];
          }

          return [
            true,
            {
              ...fieldErrors,
              moverFilters: "",
            },
          ];
        },
      },
      states: {
        dataId: "license.geography.states",
        type: "stringArray",
        validator: selectedStatesValidator,
      },
    },
  },
  [PRODUCT_IDS.MARKET_VOLUME_LITE]: {
    id: PRODUCT_IDS.MARKET_VOLUME_LITE,
    dataTableKey: null, // ? unknown
    meta: {
      name: "Market Volume Lite",
      description:
        "Enable to license Market Volume Lite and Provider Search Lite workbooks and views. This product is primarily for Population Intelligence customers who do not have any other Provider/Market dashboards but want to gain market opportunity insights or search our provider directory. Optionally configure licensed date range and service location states.",
      bundle: PRODUCT_BUNDLES[PRODUCT_BUNDLE_IDS.POP_INTEL],
    },
    page: {
      path: "market-volume-lite",
      isEnabled: { fieldKey: "isEnabled" },
      settings: [
        {
          title: "Market Volume Lite",
          description:
            "Enable to license market volume lite. This product is primarily for Population Intelligence customers who do not have any other Provider/Market dashboards. Optionally configure licensed dates to limit the underlying data between the selected dates. When no licensed date is selected, no date filter is applied. Optionally toggle on State filter to specifically license one or more US States. When this is kept off, all States will be licensed by default.",
          controls: [
            {
              fieldKey: "licensedDates",
              componentType: "date",
              staticProps: {
                name: "licensedDates",
                label: "Licensed Dates",
                isRange: true,
              },
            },
            {
              fieldKey: "states",
              componentType: "combobox",
              toggleKey: "isStateFilteringRequired",
              staticProps: {
                label: "Selected States",
                toggleLabel: "State Selection",
                placeholder: "Select States",
                name: "states",
                isMultiSelect: true,
                isSearchable: true,
                multiValueMaxDisplayCount: 15,
              },
            },
          ],
        },
      ],
    },
    fields: {
      isEnabled: {
        dataId: "enabled",
        type: "boolean",
      },
      licensedDates: {
        dataId: "license.dates",
        type: "dateRange",
        toControlFormat: dateRangeControlFormatter,
        toAPIFormat: dateRangeAPIFormatter,
        validator: dateRangeValidator,
      },
      states: {
        dataId: "license.geography.states",
        type: "stringArray",
        validator: selectedStatesValidator,
      },
    },
  },
  [PRODUCT_IDS.PROVIDER_DIRECTORY]: {
    id: PRODUCT_IDS.PROVIDER_DIRECTORY,
    dataTableKey: null, // ? unknown
    meta: {
      name: "Provider Directory",
      description: "Enable to license Provider Directory.",
      bundle: PRODUCT_BUNDLES[PRODUCT_BUNDLE_IDS.PROV_INTEL],
    },
    page: {
      path: "provider-directory",
      isEnabled: { fieldKey: "isEnabled" },
      settings: [],
    },
    fields: {
      isEnabled: {
        dataId: "enabled",
        type: "boolean",
      },
    },
  },
  [PRODUCT_IDS.PROVIDER_AFFILIATIONS]: {
    id: PRODUCT_IDS.PROVIDER_AFFILIATIONS,
    dataTableKey: null, // ? unknown
    meta: {
      name: "Provider Affiliations",
      description:
        "Enable to license Provider Affiliations. Optionally configure licensed date range.",
      bundle: PRODUCT_BUNDLES[PRODUCT_BUNDLE_IDS.NET_INTEL],
    },
    page: {
      path: "provider-affiliations",
      isEnabled: { fieldKey: "isEnabled" },
      settings: [
        {
          title: "Provider Affiliations",
          description:
            "Enable to license Provider Affiliations. Optionally configure licensed dates to limit the underlying data between the selected dates. When no licensed date is selected, no date filter is applied.",
          controls: [
            {
              fieldKey: "licensedDates",
              componentType: "date",
              staticProps: {
                name: "licensedDates",
                label: "Licensed Dates",
                isRange: true,
              },
            },
          ],
        },
      ],
    },
    fields: {
      isEnabled: {
        dataId: "enabled",
        type: "boolean",
      },
      licensedDates: {
        dataId: "license.dates",
        type: "dateRange",
        toControlFormat: dateRangeControlFormatter,
        toAPIFormat: dateRangeAPIFormatter,
        validator: dateRangeValidator,
      },
    },
  },
  [PRODUCT_IDS.PROVIDER_PATHWAYS]: {
    id: PRODUCT_IDS.PROVIDER_PATHWAYS,
    dataTableKey: null, // ? unknown
    meta: {
      name: "Provider Pathways",
      description:
        "Enable to license provider pathways. Optionally configure licensed date ranges and inbound service location states.",
      bundle: PRODUCT_BUNDLES[PRODUCT_BUNDLE_IDS.NET_INTEL],
    },
    page: {
      path: "provider-pathways",
      isEnabled: { fieldKey: "isEnabled" },
      settings: [
        {
          title: "Provider Pathways",
          description:
            "Enable to license provider pathways. Optionally configure licensed dates to limit the underlying data between the selected dates. When no licensed date is selected, no date filter is applied. Optionally toggle on State filter to specifically license one or more US States. When this is kept off, all States will be licensed by default.",
          controls: [
            {
              fieldKey: "licensedDates",
              componentType: "date",
              staticProps: {
                name: "licensedDates",
                label: "Licensed Dates",
                isRange: true,
              },
            },
            {
              fieldKey: "states",
              componentType: "combobox",
              toggleKey: "isStateFilteringRequired",
              staticProps: {
                label: "Selected States",
                toggleLabel: "State Selection",
                placeholder: "Select States",
                name: "states",
                isMultiSelect: true,
                isSearchable: true,
                multiValueMaxDisplayCount: 15,
              },
            },
          ],
        },
      ],
    },
    fields: {
      isEnabled: {
        dataId: "enabled",
        type: "boolean",
      },
      licensedDates: {
        dataId: "license.dates",
        type: "dateRange",
        toControlFormat: dateRangeControlFormatter,
        toAPIFormat: dateRangeAPIFormatter,
        validator: dateRangeValidator,
      },
      states: {
        dataId: "license.geography.states",
        type: "stringArray",
        validator: selectedStatesValidator,
      },
    },
  },
  [PRODUCT_IDS.PROVIDER_BENCHMARKS]: {
    id: PRODUCT_IDS.PROVIDER_BENCHMARKS,
    dataTableKey: null, // ? unknown
    meta: {
      name: "Provider Benchmarks",
      description:
        "Enable to license Provider Benchmarks. Optionally configure licensed date range.",
      bundle: PRODUCT_BUNDLES[PRODUCT_BUNDLE_IDS.NET_INTEL],
    },
    page: {
      path: "provider-benchmarks",
      isEnabled: { fieldKey: "isEnabled" },
      settings: [
        {
          title: "Provider Benchmarks",
          description:
            "Enable to license Provider Benchmarks. Optionally configure licensed dates to limit the underlying data between the selected dates. When no licensed date is selected, no date filter is applied.",

          controls: [
            {
              fieldKey: "licensedDates",
              componentType: "date",
              staticProps: {
                name: "licensedDates",
                label: "Licensed Dates",
                isRange: true,
              },
            },
          ],
        },
      ],
    },
    fields: {
      isEnabled: {
        dataId: "enabled",
        type: "boolean",
      },
      licensedDates: {
        dataId: "license.dates",
        type: "dateRange",
        toControlFormat: dateRangeControlFormatter,
        toAPIFormat: dateRangeAPIFormatter,
        validator: dateRangeValidator,
      },
    },
  },
  [PRODUCT_IDS.MARKET_VOLUME]: {
    id: PRODUCT_IDS.MARKET_VOLUME,
    dataTableKey: null, // ? unknown
    meta: {
      name: "Market Volume",
      description:
        "Enable to license Market Volume. Optionally configure licensed date range and service location States.",
      bundle: PRODUCT_BUNDLES[PRODUCT_BUNDLE_IDS.MARK_INTEL],
    },
    page: {
      path: "market-volume",
      isEnabled: { fieldKey: "isEnabled" },
      settings: [
        {
          title: "Market Volume",
          description:
            "Enable to license Market Volume. Optionally configure licensed dates to limit the underlying data between the selected dates. When no licensed date is selected, no date filter is applied. Optionally toggle on State filter to specifically license one or more US States. When this is kept off, all States will be licensed by default.",
          controls: [
            {
              fieldKey: "licensedDates",
              componentType: "date",
              staticProps: {
                name: "licensedDates",
                label: "Licensed Dates",
                isRange: true,
              },
            },
            {
              fieldKey: "states",
              componentType: "combobox",
              toggleKey: "isStateFilteringRequired",
              staticProps: {
                label: "Selected States",
                toggleLabel: "State Selection",
                placeholder: "Select States",
                name: "states",
                isMultiSelect: true,
                isSearchable: true,
                multiValueMaxDisplayCount: 15,
              },
            },
          ],
        },
      ],
    },
    fields: {
      isEnabled: {
        dataId: "enabled",
        type: "boolean",
      },
      licensedDates: {
        dataId: "license.dates",
        type: "dateRange",
        toControlFormat: dateRangeControlFormatter,
        toAPIFormat: dateRangeAPIFormatter,
        validator: dateRangeValidator,
      },
      states: {
        dataId: "license.geography.states",
        type: "stringArray",
        validator: selectedStatesValidator,
      },
    },
  },
  [PRODUCT_IDS.CLAIM_VOLUME]: {
    id: PRODUCT_IDS.CLAIM_VOLUME,
    dataTableKey: null,
    meta: {
      name: "Claim Volume",
      description:
        "Enable to license Claim Volume. Optionally configure licensed date range and service location States.",
      bundle: PRODUCT_BUNDLES[PRODUCT_BUNDLE_IDS.MARK_INTEL],
    },
    page: {
      path: "claim-volume",
      isEnabled: { fieldKey: "isEnabled" },
      settings: [
        {
          title: "Claim Volume",
          description:
            "Enable to license Claim Volume. Optionally configure licensed dates to limit the underlying data between the selected dates. When no licensed date is selected, no date filter is applied. Optionally toggle on State filter to specifically license one or more US States. When this is kept off, all States will be licensed by default.",
          controls: [
            {
              fieldKey: "licensedDates",
              componentType: "date",
              staticProps: {
                name: "licensedDates",
                label: "Licensed Dates",
                isRange: true,
              },
            },
            {
              fieldKey: "states",
              componentType: "combobox",
              toggleKey: "isStateFilteringRequired",
              staticProps: {
                label: "Selected Service Location States",
                toggleLabel: "State Selection",
                placeholder: "Select States",
                name: "states",
                isMultiSelect: true,
                isSearchable: true,
                multiValueMaxDisplayCount: 15,
              },
            },
          ],
        },
      ],
    },
    fields: {
      isEnabled: {
        dataId: "enabled",
        type: "boolean",
      },
      licensedDates: {
        dataId: "license.dates",
        type: "dateRange",
        toControlFormat: dateRangeControlFormatter,
        toAPIFormat: dateRangeAPIFormatter,
        validator: dateRangeValidator,
      },
      states: {
        dataId: "license.geography.states",
        type: "stringArray",
        validator: selectedStatesValidator,
      },
    },
  },
  [PRODUCT_IDS.PATIENT_VOLUME]: {
    id: PRODUCT_IDS.PATIENT_VOLUME,
    dataTableKey: null, // ? unknown
    meta: {
      name: "Patient Volume",
      description:
        "Enable to license Patient Volume. Optionally configure licensed date range and inbound service location States.",
      bundle: PRODUCT_BUNDLES[PRODUCT_BUNDLE_IDS.MARK_INTEL],
    },
    page: {
      path: "market-patient-volume",
      isEnabled: { fieldKey: "isEnabled" },
      settings: [
        {
          title: "Patient Volume",
          description:
            "Enable to license Patient Volume. Optionally configure licensed dates to limit the underlying data between the selected dates. When no licensed date is selected, no date filter is applied. Optionally toggle on State filter to specifically license one or more US States. When this is kept off, all States will be licensed by default.",
          controls: [
            {
              fieldKey: "licensedDates",
              componentType: "date",
              staticProps: {
                name: "licensedDates",
                label: "Licensed Dates",
                isRange: true,
              },
            },
            {
              fieldKey: "states",
              componentType: "combobox",
              toggleKey: "isStateFilteringRequired",
              staticProps: {
                label: "Selected States",
                toggleLabel: "State Selection",
                placeholder: "Select States",
                name: "states",
                isMultiSelect: true,
                isSearchable: true,
                multiValueMaxDisplayCount: 15,
              },
            },
          ],
        },
      ],
    },
    fields: {
      isEnabled: {
        dataId: "enabled",
        type: "boolean",
      },
      licensedDates: {
        dataId: "license.dates",
        type: "dateRange",
        toControlFormat: dateRangeControlFormatter,
        toAPIFormat: dateRangeAPIFormatter,
        validator: dateRangeValidator,
      },
      states: {
        dataId: "license.geography.states",
        type: "stringArray",
        validator: selectedStatesValidator,
      },
    },
  },
  [PRODUCT_IDS.MARKET_VOLUME_ONCOLOGY]: {
    id: PRODUCT_IDS.MARKET_VOLUME_ONCOLOGY,
    dataTableKey: null, // ? unknown
    meta: {
      name: "Oncology Insights",
      description:
        "Enable to license Oncology Insights. Optionally configure licensed date range and service location States.",
      bundle: PRODUCT_BUNDLES[PRODUCT_BUNDLE_IDS.MARK_INTEL],
    },
    page: {
      path: "market-volume-oncology",
      isEnabled: { fieldKey: "isEnabled" },
      settings: [
        {
          title: "Oncology Insights",
          description:
            "Enable to license Oncology Insights. Optionally configure licensed dates to limit the underlying data between the selected dates. When no licensed date is selected, no date filter is applied. Optionally toggle on State filter to specifically license one or more US States. When this is kept off, all States will be licensed by default.",
          controls: [
            {
              fieldKey: "licensedDates",
              componentType: "date",
              staticProps: {
                name: "licensedDates",
                label: "Licensed Dates",
                isRange: true,
              },
            },
            {
              fieldKey: "states",
              componentType: "combobox",
              toggleKey: "isStateFilteringRequired",
              staticProps: {
                label: "Selected States",
                toggleLabel: "State Selection",
                placeholder: "Select States",
                name: "states",
                isMultiSelect: true,
                isSearchable: true,
                multiValueMaxDisplayCount: 15,
              },
            },
          ],
        },
      ],
    },
    fields: {
      isEnabled: {
        dataId: "enabled",
        type: "boolean",
      },
      licensedDates: {
        dataId: "license.dates",
        type: "dateRange",
        toControlFormat: dateRangeControlFormatter,
        toAPIFormat: dateRangeAPIFormatter,
        validator: dateRangeValidator,
      },
      states: {
        dataId: "license.geography.states",
        type: "stringArray",
        validator: selectedStatesValidator,
      },
    },
  },
  [PRODUCT_IDS.PRICE_TRANSPARENCY]: {
    id: PRODUCT_IDS.PRICE_TRANSPARENCY,
    dataTableKey: null, // ? unknown
    meta: {
      name: "Price Transparency",
      description:
        "Enable to license Price Transparency. Optionally configure licensed date range.",
      bundle: PRODUCT_BUNDLES[PRODUCT_BUNDLE_IDS.MARK_INTEL],
    },
    page: {
      path: "price-transparency",
      isEnabled: { fieldKey: "isEnabled" },
      settings: [
        {
          title: "Price Transparency",
          description:
            "Enable to license Provider Transparency. Optionally configure licensed dates to limit the underlying data between the selected dates. When no licensed date is selected, no date filter is applied.",
          controls: [
            {
              fieldKey: "licensedDates",
              componentType: "date",
              staticProps: {
                name: "licensedDates",
                label: "Licensed Dates",
                isRange: true,
              },
            },
          ],
        },
      ],
    },
    fields: {
      isEnabled: {
        dataId: "enabled",
        type: "boolean",
      },
      licensedDates: {
        dataId: "license.dates",
        type: "dateRange",
        toControlFormat: dateRangeControlFormatter,
        toAPIFormat: dateRangeAPIFormatter,
        validator: dateRangeValidator,
      },
    },
  },
  [PRODUCT_IDS.MARKET_FORECAST]: {
    id: PRODUCT_IDS.MARKET_FORECAST,
    dataTableKey: null, // ? unknown
    meta: {
      name: "Market Forecast",
      description:
        "Enable to license Market Forecast. Optionally configure licensed date range and patient origin by States.",
      bundle: PRODUCT_BUNDLES[PRODUCT_BUNDLE_IDS.MARK_FORECAST],
    },
    page: {
      path: "market-forecast",
      isEnabled: { fieldKey: "isEnabled" },
      settings: [
        {
          title: "Market Forecast",
          description:
            "Enable to license Market Forecast. Optionally configure licensed dates to limit the underlying data between the selected dates. When no licensed date is selected, no date filter is applied. Optionally toggle on State filter to specifically license one or more US States. When this is kept off, all States will be licensed by default. States are based on the State of the underlying Patients in the dataset.",
          controls: [
            {
              fieldKey: "licensedDates",
              componentType: "date",
              staticProps: {
                name: "licensedDates",
                label: "Licensed Dates",
                isRange: true,
              },
            },
            {
              fieldKey: "states",
              componentType: "combobox",
              toggleKey: "isStateFilteringRequired",
              staticProps: {
                label: "Selected States",
                toggleLabel: "State Selection",
                placeholder: "Select States",
                name: "states",
                isMultiSelect: true,
                isSearchable: true,
                multiValueMaxDisplayCount: 15,
              },
            },
          ],
        },
      ],
    },
    fields: {
      isEnabled: {
        dataId: "enabled",
        type: "boolean",
      },
      licensedDates: {
        dataId: "license.dates",
        type: "dateRange",
        toControlFormat: dateRangeControlFormatter,
        toAPIFormat: dateRangeAPIFormatter,
        validator: dateRangeValidator,
      },
      states: {
        dataId: "license.geography.states",
        type: "stringArray",
        validator: selectedStatesValidator,
      },
    },
  },
};
